import { useLanguage } from '../lib/language';
import { useAuth } from '../lib/auth';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { 
  Camera, 
  Images, 
  Heart, 
  CreditCard, 
  Sparkles, 
  Palette, 
  BarChart3,
  ChevronRight,
  Globe,
  Check
} from 'lucide-react';

const FeatureCard = ({ icon: Icon, title, description, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    viewport={{ once: true }}
    className="group p-6 bg-card border border-border rounded-sm hover:border-primary/30 transition-colors duration-300"
  >
    <div className="w-12 h-12 mb-4 flex items-center justify-center bg-secondary rounded-sm group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
      <Icon className="w-6 h-6" />
    </div>
    <h3 className="text-lg font-semibold mb-2 font-sans">{title}</h3>
    <p className="text-muted-foreground text-sm leading-relaxed">{description}</p>
  </motion.div>
);

const PricingCard = ({ plan, price, description, features, highlighted, delay, t }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    viewport={{ once: true }}
    className={`p-6 rounded-sm border ${
      highlighted 
        ? 'bg-primary text-primary-foreground border-primary' 
        : 'bg-card border-border'
    }`}
  >
    <h3 className="text-xl font-bold mb-1">{plan}</h3>
    <p className={`text-sm mb-4 ${highlighted ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
      {description}
    </p>
    <div className="mb-6">
      <span className="text-4xl font-bold">{price}</span>
      {price > 0 && <span className={`text-sm ${highlighted ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}> zł/{t('landing.pricing.monthly')}</span>}
    </div>
    <ul className="space-y-3 mb-6">
      {features.map((feature, idx) => (
        <li key={idx} className="flex items-center gap-2 text-sm">
          <Check className={`w-4 h-4 ${highlighted ? 'text-primary-foreground' : 'text-primary'}`} />
          {feature}
        </li>
      ))}
    </ul>
    <Button 
      variant={highlighted ? 'secondary' : 'outline'} 
      className="w-full"
      data-testid={`pricing-${plan.toLowerCase()}-btn`}
    >
      {t('landing.hero.cta')}
    </Button>
  </motion.div>
);

export default function LandingPage() {
  const { t, language, setLanguage } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Camera className="w-6 h-6" />
            <span className="font-bold text-lg tracking-tight">Focus Photo</span>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setLanguage(language === 'pl' ? 'en' : 'pl')}
              data-testid="language-toggle"
            >
              <Globe className="w-4 h-4 mr-2" />
              {language.toUpperCase()}
            </Button>
            {user ? (
              <Button onClick={() => navigate('/dashboard')} data-testid="dashboard-btn">
                {t('dashboard.title')}
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')} data-testid="login-nav-btn">
                  {t('auth.login')}
                </Button>
                <Button onClick={() => navigate('/register')} data-testid="register-nav-btn">
                  {t('auth.register')}
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(https://images.unsplash.com/photo-1741504303353-b06138843ed7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxwaG90b2dyYXBoZXIlMjBlZGl0aW5nJTIwcGhvdG9zJTIwd29ya3NwYWNlfGVufDB8fHx8MTc2NjM0MTk0MHww&ixlib=rb-4.1.0&q=85)` 
          }}
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-5xl md:text-7xl font-bold tracking-tight mb-6"
          >
            {t('landing.hero.title')}{' '}
            <span className="text-primary">{t('landing.hero.titleHighlight')}</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10"
          >
            {t('landing.hero.subtitle')}
          </motion.p>
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button 
              size="lg" 
              onClick={() => navigate('/register')}
              className="px-8 bg-white text-black hover:bg-white/90"
              data-testid="hero-cta-btn"
            >
              {t('landing.hero.cta')}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => navigate('/gallery/demo')}
              data-testid="hero-demo-btn"
            >
              {t('landing.hero.ctaSecondary')}
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">{t('landing.features.title')}</h2>
            <p className="text-muted-foreground text-lg">{t('landing.features.subtitle')}</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard 
              icon={Images} 
              title={t('landing.features.gallery.title')} 
              description={t('landing.features.gallery.description')} 
              delay={0}
            />
            <FeatureCard 
              icon={Heart} 
              title={t('landing.features.selection.title')} 
              description={t('landing.features.selection.description')} 
              delay={0.1}
            />
            <FeatureCard 
              icon={CreditCard} 
              title={t('landing.features.sales.title')} 
              description={t('landing.features.sales.description')} 
              delay={0.2}
            />
            <FeatureCard 
              icon={Sparkles} 
              title={t('landing.features.ai.title')} 
              description={t('landing.features.ai.description')} 
              delay={0.3}
            />
            <FeatureCard 
              icon={Palette} 
              title={t('landing.features.branding.title')} 
              description={t('landing.features.branding.description')} 
              delay={0.4}
            />
            <FeatureCard 
              icon={BarChart3} 
              title={t('landing.features.stats.title')} 
              description={t('landing.features.stats.description')} 
              delay={0.5}
            />
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 px-6 bg-card/50">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">{t('landing.pricing.title')}</h2>
            <p className="text-muted-foreground text-lg">{t('landing.pricing.subtitle')}</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <PricingCard 
              plan={t('landing.pricing.free.name')}
              price={0}
              description={t('landing.pricing.free.description')}
              features={t('landing.pricing.free.features')}
              delay={0}
              t={t}
            />
            <PricingCard 
              plan={t('landing.pricing.lite.name')}
              price={29}
              description={t('landing.pricing.lite.description')}
              features={t('landing.pricing.lite.features')}
              delay={0.1}
              t={t}
            />
            <PricingCard 
              plan={t('landing.pricing.pro.name')}
              price={79}
              description={t('landing.pricing.pro.description')}
              features={t('landing.pricing.pro.features')}
              highlighted
              delay={0.2}
              t={t}
            />
            <PricingCard 
              plan={t('landing.pricing.studio.name')}
              price={149}
              description={t('landing.pricing.studio.description')}
              features={t('landing.pricing.studio.features')}
              delay={0.3}
              t={t}
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold mb-4"
          >
            {t('landing.cta.title')}
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            viewport={{ once: true }}
            className="text-muted-foreground text-lg mb-8"
          >
            {t('landing.cta.subtitle')}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Button 
              size="lg" 
              onClick={() => navigate('/register')}
              className="px-10"
              data-testid="cta-register-btn"
            >
              {t('landing.cta.button')}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-border">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5" />
            <span className="font-semibold">Focus Photo</span>
          </div>
          <p className="text-sm text-muted-foreground">{t('landing.footer.copyright')}</p>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <a href="/privacy" className="hover:text-foreground transition-colors">{t('landing.footer.privacy')}</a>
            <a href="/terms" className="hover:text-foreground transition-colors">{t('landing.footer.terms')}</a>
            <a href="/contact" className="hover:text-foreground transition-colors">{t('landing.footer.contact')}</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
